<template>
  <div class="importacao-container">
    <!-- Header da página -->
    <div class="page-header">
      <div class="header-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line
            x1="12"
            y1="15"
            x2="12"
            y2="3"
          />
        </svg>
      </div>
      <div class="header-text">
        <h1>Importação de Dados SIGAA</h1>
        <p>
          Selecione os arquivos CSV dos relatórios para atualizar os dados no
          sistema
        </p>
      </div>
    </div>

    <!-- Lista de relatórios (Cards Retangulares Horizontais) -->
    <div class="relatorios-lista">
      <div
        v-for="relatorio in relatorios"
        :key="relatorio.id"
        class="relatorio-card"
        :class="{
          'card-importing': relatorio.importando,
          'card-done': relatorio.concluido,
        }"
      >
        <!-- Conteúdo principal do card (Esquerda) -->
        <div class="card-info">
          <div
            class="card-icon"
            :style="{ background: relatorio.gradiente }"
          >
            <span v-html="relatorio.icone"></span>
          </div>
          <div class="card-details">
            <h3 class="card-title">{{ relatorio.titulo }}</h3>
            <p class="card-desc">{{ relatorio.descricao }}</p>

            <!-- Mostra arquivo selecionado se importado com sucesso -->
            <div
              v-if="relatorio.concluido"
              class="import-success-info"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>Importado: {{ relatorio.arquivoNome }}</span>
            </div>
          </div>
        </div>

        <!-- Ações do card (Direita) -->
        <div class="card-actions">
          <input
            type="file"
            :id="'fileInput-' + relatorio.id"
            accept=".csv"
            @change="onFileSelected($event, relatorio)"
            style="display: none"
          />

          <!-- Barra de progresso inline se importando -->
          <div
            v-if="relatorio.importando"
            class="progress-wrapper"
          >
            <div class="progress-bar-container">
              <div
                class="progress-bar-fill"
                :style="{ width: relatorio.progresso + '%' }"
              ></div>
            </div>
            <span class="progress-text"
              >Importando {{ Math.round(relatorio.progresso) }}%</span
            >
          </div>

          <!-- Botão único de importar/concluído -->
          <button
            v-else
            class="btn-importar-acao"
            :class="{ 'btn-concluido': relatorio.concluido }"
            @click="triggerImport(relatorio.id)"
          >
            <!-- Ícone correspondente -->
            <svg
              v-if="!relatorio.concluido"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line
                x1="12"
                y1="3"
                x2="12"
                y2="15"
              />
            </svg>
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>{{
              relatorio.concluido ? "Importado" : "Importar CSV"
            }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Botão de finalização (Exportar GEX) -->
    <div
      v-if="todosConcluidos"
      class="finalizacao-section"
    >
      <div class="finalizacao-alert">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        <span
          >Todos os relatórios foram importados individualmente. Você já pode
          concluir e exportar os dados para o GEX.</span
        >
      </div>
      <button
        class="btn-exportar-gex"
        @click="exportarParaGex"
        :disabled="exportando"
      >
        <span
          v-if="exportando"
          class="spinner-mini"
        ></span>
        <span v-else>Exportar GEX</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, onMounted } from "vue";
import sigaaService from "@/service/sigaaService";

const relatorios = reactive([
  {
    id: "atividade",
    titulo: "Relatório de Atividades da Instituição",
    descricao: "Dados das atividades de extensão registradas na instituição",
    nomeArquivo: "CONSULTA_RELATORIO_ATIVIDADES_INSTITUICAO",
    icone:
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="14" x2="8" y2="14.01"/><line x1="12" y1="14" x2="12" y2="14.01"/><line x1="16" y1="14" x2="16" y2="14.01"/><line x1="8" y1="18" x2="8" y2="18.01"/><line x1="12" y1="18" x2="12" y2="18.01"/></svg>',
    gradiente: "linear-gradient(135deg, #6f319e, #9b59b6)",
    importando: false,
    progresso: 0,
    concluido: false,
    arquivoNome: "",
  },
  {
    id: "autorizacao",
    titulo: "Relatório de Autorizações de Extensão",
    descricao: "Registros de autorizações concedidas para ações de extensão",
    nomeArquivo: "CONSULTA_RELATORIO_AUTORIZACOES_EXTENSAO",
    icone:
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>',
    gradiente: "linear-gradient(135deg, #3b2479, #6f319e)",
    importando: false,
    progresso: 0,
    concluido: false,
    arquivoNome: "",
  },
  /*
  {
    id: 'local',
    titulo: 'Relatório de Locais de Realização',
    descricao: 'Informações sobre os locais onde são realizadas as ações',
    nomeArquivo: 'CONSULTA_RELATORIO_LOCAIS_REALIZACAO',
    icone: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>',
    gradiente: 'linear-gradient(135deg, #48298a, #772fa2)',
    importando: false,
    progresso: 0,
    concluido: false,
    arquivoNome: '',
  },
  */
  {
    id: "membro",
    titulo: "Relatório de Membros de Extensão",
    descricao: "Dados dos membros participantes das ações extensionistas",
    nomeArquivo: "CONSULTA_RELATORIO_MEMBROS_EXTENSAO",
    icone:
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
    gradiente: "linear-gradient(135deg, #513187, #9b59b6)",
    importando: false,
    progresso: 0,
    concluido: false,
    arquivoNome: "",
  },
  {
    id: "curricularizacao",
    titulo: "Relatório de Curricularização",
    descricao: "Dados do relatório de curricularização de extensão",
    nomeArquivo: "CONSULTA_RELATORIO_CURRICULARIZACAO",
    icone:
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>',
    gradiente: "linear-gradient(135deg, #3b2479, #8e44ad)",
    importando: false,
    progresso: 0,
    concluido: false,
    arquivoNome: "",
  },
  /*
  {
    id: 'orcamento',
    titulo: 'Relatório de Orçamento',
    descricao: 'Informações orçamentárias das ações de extensão',
    nomeArquivo: 'CONSULTA_RELATORIO_ORCAMENTO',
    icone: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>',
    gradiente: 'linear-gradient(135deg, #44257a, #6f319e)',
    importando: false,
    progresso: 0,
    concluido: false,
    arquivoNome: '',
  },
  {
    id: 'parceira',
    titulo: 'Relatório de Parceiras Externas',
    descricao: 'Dados de instituições parceiras nas ações de extensão',
    nomeArquivo: 'CONSULTA_RELATORIO_PARCEIRAS_EXTERNAS',
    icone: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>',
    gradiente: 'linear-gradient(135deg, #3a2474, #48298a)',
    importando: false,
    progresso: 0,
    concluido: false,
    arquivoNome: '',
  },
  */
]);

const exportando = ref(false);

const todosConcluidos = computed(() => {
  return relatorios.length > 0 && relatorios.every((r) => r.concluido);
});

onMounted(() => {
  // Limpa do localStorage ao montar a página para garantir que comece limpo (refresh ou novo acesso)
  relatorios.forEach((relatorio) => {
    localStorage.removeItem(`gex_sigaa_filename_${relatorio.id}`);
    localStorage.removeItem(`gex_sigaa_csv_${relatorio.id}`);
    relatorio.concluido = false;
    relatorio.arquivoNome = "";
    relatorio.progresso = 0;
  });
});

function triggerImport(id) {
  const fileInput = document.getElementById(`fileInput-${id}`);
  if (fileInput) {
    fileInput.value = "";
    fileInput.click();
  }
}

function onFileSelected(event, relatorio) {
  const file = event.target.files[0];
  if (!file) return;

  if (!file.name.endsWith(".csv")) {
    alert("Por favor, selecione um arquivo no formato CSV.");
    return;
  }

  const confirmado = confirm(
    `Deseja carregar o arquivo "${file.name}" para "${relatorio.titulo}"?`
  );
  if (!confirmado) return;

  iniciarImportacao(relatorio, file);
}

function lerArquivoComoTexto(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
}

async function iniciarImportacao(relatorio, file) {
  relatorio.importando = true;
  relatorio.progresso = 10;
  relatorio.concluido = false;
  relatorio.arquivoNome = file.name;

  const interval = setInterval(() => {
    if (relatorio.progresso < 90) {
      relatorio.progresso += Math.random() * 20 + 5;
    }
  }, 100);

  try {
    const textContent = await lerArquivoComoTexto(file);

    // Armazena no localStorage
    localStorage.setItem(`gex_sigaa_csv_${relatorio.id}`, textContent);
    localStorage.setItem(`gex_sigaa_filename_${relatorio.id}`, file.name);

    clearInterval(interval);
    relatorio.progresso = 100;
    setTimeout(() => {
      relatorio.importando = false;
      relatorio.concluido = true;
      alert(
        `Relatório "${relatorio.titulo}" carregado localmente com sucesso!`
      );
    }, 300);
  } catch (error) {
    clearInterval(interval);
    relatorio.importando = false;
    relatorio.progresso = 0;
    relatorio.concluido = false;
    alert(
      `Erro ao ler o arquivo "${file.name}": ${error.message || "Erro desconhecido."}`
    );
  }
}

async function exportarParaGex() {
  exportando.value = true;
  try {
    const formData = new FormData();
    const mapCampos = {
      atividade: "fileAtividades",
      autorizacao: "fileAutorizacoes",
      membro: "fileMembros",
      curricularizacao: "fileCurricularizadas",
    };

    for (const relatorio of relatorios) {
      const csvText = localStorage.getItem(`gex_sigaa_csv_${relatorio.id}`);
      const fileName = localStorage.getItem(
        `gex_sigaa_filename_${relatorio.id}`
      );

      if (csvText && fileName && mapCampos[relatorio.id]) {
        const file = new File([csvText], fileName, { type: "text/csv" });
        formData.append(mapCampos[relatorio.id], file);
      }
    }

    const res = await sigaaService.importarLote(formData);

    alert(
      res || "Importação iniciada com sucesso! Os dados estão sendo processados em background."
    );

    // Limpa o localStorage e os estados após o sucesso completo
    relatorios.forEach((r) => {
      localStorage.removeItem(`gex_sigaa_csv_${r.id}`);
      localStorage.removeItem(`gex_sigaa_filename_${r.id}`);
      r.concluido = false;
      r.arquivoNome = "";
      r.progresso = 0;
    });
  } catch (error) {
    console.error("Erro na finalização do SIGAA:", error);
    alert(
      `Erro ao exportar dados para o GEX: ${error.data?.message || error.message || "Erro de conexão."}`
    );
  } finally {
    exportando.value = false;
  }
}
</script>

<style scoped>
.importacao-container {
  padding: 8px;
  background: transparent;
  font-family: var(--font-base);
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ========== HEADER ========== */
.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  color: white;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  flex-shrink: 0;
}

.header-text h1 {
  font-size: 1.35rem;
  font-weight: 600;
  margin: 0 0 2px;
}

.header-text p {
  font-size: 0.85rem;
  margin: 0;
  opacity: 0.7;
}

/* ========== LISTA DE RELATÓRIOS ========== */
.relatorios-lista {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ========== CARD RETANGULAR ========== */
.relatorio-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #25164f;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 16px 20px;
  transition: all 0.25s ease;
  gap: 16px;
}

.relatorio-card:hover {
  background: #2d1a6e;
  border-color: rgba(255, 255, 255, 0.15);
  transform: translateX(4px);
}

.relatorio-card.card-importing {
  border-color: rgba(124, 58, 237, 0.5);
  background: #281657;
}

.relatorio-card.card-done {
  border-color: rgba(34, 197, 94, 0.4);
}

/* Card Info (Esquerda) */
.card-info {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
}

.card-icon {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.card-icon :deep(svg) {
  width: 20px;
  height: 20px;
}

.card-details {
  min-width: 0;
}

.card-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: white;
  margin: 0 0 4px;
}

.card-desc {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.import-success-info {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 0.75rem;
  color: #4ade80;
  font-weight: 500;
}

/* Card Actions (Direita) */
.card-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

/* Progress bar inline */
.progress-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  width: 140px;
}

.progress-bar-container {
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #a78bfa, #c084fc);
  border-radius: 3px;
  transition: width 0.2s ease;
}

.progress-text {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.7);
}

/* Botão Importar */
.btn-importar-acao {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #7c3aed;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-importar-acao:hover {
  background: #6d28d9;
  transform: translateY(-1px);
}

.btn-importar-acao:active {
  transform: translateY(0);
}

.btn-importar-acao.btn-concluido {
  background: rgba(34, 197, 94, 0.2);
  color: #4ade80;
  border: 1px solid rgba(34, 197, 94, 0.4);
  cursor: default;
}

.btn-importar-acao.btn-concluido:hover {
  transform: none;
}

/* ========== FINALIZAÇÃO IMPORTAÇÃO ========== */
.finalizacao-section {
  margin-top: 10px;
  padding: 20px;
  background: rgba(34, 197, 94, 0.05);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  animation: fadeIn 0.4s ease-in-out;
}

.finalizacao-alert {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #86efac;
  font-size: 0.88rem;
  text-align: center;
  line-height: 1.4;
}

.btn-exportar-gex {
  background: #22c55e;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px 36px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px rgba(34, 197, 94, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
}

.btn-exportar-gex:hover:not(:disabled) {
  background: #16a34a;
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(34, 197, 94, 0.4);
}

.btn-exportar-gex:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.spinner-mini {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ========== RESPONSIVO ========== */
@media (max-width: 768px) {
  .relatorio-card {
    flex-direction: column;
    align-items: stretch;
  }

  .card-actions {
    justify-content: flex-end;
    margin-top: 10px;
  }

  .progress-wrapper {
    width: 100%;
  }
}
</style>
